/*
 * File: ContributorDashboard.jsx
 * Project: our-wave-stories-app
 *
 * Created by Brendan Michaelsen on June 13, 2024 at 9:21 AM
 * Copyright © 2024 Our Wave, Inc. All rights reserved.
 *
 * Last Modified: June 4, 2025 at 4:06 PM
 * Modified By: Brendan Michaelsen
 */

/**
 * Imports
 */

// Modules
import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useDebouncedCallback } from 'beautiful-react-hooks';

// Utilities
import { formatTagFamilies } from '../../../utilities/tags';
import { toastError, toastSuccess } from '../../../utilities/toaster';

// Services
import { handleContributorActions } from '../../../services/user';

// Store
import { fetchTags } from '../../../store/slices/storyTag/storyTag.slice';

// Components
import {
	Meta,
	AppNavigation,
	StoryComponent,
	Typography,
	Checkbox,
} from '../../../components';

// Constants
import { AVAILABLE_I18N_NAMESPACES, UPDATE_ACTIONS } from '../../../../constants/base.constants';

// Styles
import * as S from './ContributorDashboard.styles';

/**
 * Component
 */

const ContributorDashboard = ({ meta: m, data }) => {

	// Parse data from router
	const {
		story,
		preferences,
		a_control: aControl,
		b_control: bControl
	} = data;

	// Create state handlers
	const [shouldUpdateOptions, setShouldUpdateOptions] = useState(false);
	const [inputValues, setInputValues] = useState({
		commentsAllowed: preferences.comments_allowed,
		isVisible: preferences.is_viewable
	});

	// Get current partner from hook
	const partner = useSelector((state) => state.partner.value);

	// Get current UI mode from hook
	const uiMode = useSelector((state) => state.ui.value);

	// Use hooks
	const dispatch = useDispatch();

	// Get current story tags from hook
	const { status: tagStatus, value: storyTags } = useSelector((state) => state.storyTag);

	// Get translation hook
	const [t] = useTranslation(AVAILABLE_I18N_NAMESPACES.CLIENT);

	// Define meta content
	const meta = useMemo(() => ({
		title: t('pageContributor.metaTitle', { partnerName: partner.name }),
		description: t('pageContributor.metaDescription', { partnerName: partner.name }),
		bodyClasses: 'hide-captcha',
		...m || {}
	}), [m, partner, t]);

	// Organize tags
	const tagFamilies = useMemo(() => formatTagFamilies(storyTags, t, partner?.id), [storyTags, t, partner]);

	// Handle on input change action
	const handleOnChange = (event) => {
		const { name, checked } = event.target;
		setInputValues({ ...inputValues, [name]: checked });
	};

	// Handle debounce function for updating notification preferences
	const debounceUpdateOptions = useDebouncedCallback(async (inputs) => {

		// Get parameters from inputs
		const {
			commentsAllowed,
			isVisible
		} = inputs;

		// Handle actions
		try {
			handleContributorActions({
				aControl,
				bControl,
				actions: [
					...commentsAllowed ? [
						UPDATE_ACTIONS.ENABLE_COMMENTS
					] : [
						UPDATE_ACTIONS.DISABLE_COMMENTS
					],
					...isVisible ? [
						UPDATE_ACTIONS.SHOW_STORY
					] : [
						UPDATE_ACTIONS.HIDE_STORY
					]
				]
			});

			// Show success toast
			toastSuccess(uiMode, partner, 'Your preferences have been updated.');

		} catch (e) {

			// Show error state
			toastError(uiMode, partner, 'We\'re having trouble updating your preferences. Please try again.');
		}
	}, [], 1000);

	// Handle actions on input value change
	useEffect(() => {
		if (shouldUpdateOptions) {

			// Update options
			debounceUpdateOptions(inputValues);
		}

		// Update permission state
		setShouldUpdateOptions(true);

	}, [inputValues]);

	// Handle actions on component load
	useEffect(() => {

		// Fetch story tags if necessary
		if (tagStatus !== 'final') {

			// Fetch story tags
			dispatch(fetchTags());
		}

	}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} data={{}} />

			{/* Component Content */}
			<AppNavigation showFooter>
				<S.Wrapper>

					{/* Story Card */}
					<S.StoryCard>
						<StoryComponent story={story} />
					</S.StoryCard>

					{/* Options Panel */}
					<S.OptionsPanel>
						<Typography tag="p" weight="semibold">Story options</Typography>
						<S.OptionsContainer>
							<S.OptionRow>
								<Checkbox
									name="isVisible"
									value={inputValues.isVisible}
									onChange={handleOnChange}
								/>
								<S.OptionContent>
									<Typography tag="p" weight="semibold">
										Story visibility
									</Typography>
									<Typography tag="p" variation="2">
										Update whether your story can be seen publicly on Our Wave.
										{' '}
										{inputValues.isVisible ? 'Your story is publicly visible' : 'Your story is hidden'}
										.
									</Typography>
								</S.OptionContent>
							</S.OptionRow>
							<S.OptionRow>
								<Checkbox
									name="commentsAllowed"
									value={inputValues.commentsAllowed}
									onChange={handleOnChange}
								/>
								<S.OptionContent>
									<Typography tag="p" weight="semibold">
										Allow community messages on my story
									</Typography>
									<Typography tag="p" variation="2">
										Update whether the community can post messages of support in response to your story. on Our Wave.
										{' '}
										{inputValues.commentsAllowed ? 'Community messages are currently allowed' : 'Community messages are disabled'}
										.
									</Typography>
								</S.OptionContent>
							</S.OptionRow>
						</S.OptionsContainer>
					</S.OptionsPanel>

				</S.Wrapper>
			</AppNavigation>
		</>
	);
};


/**
 * Configuration
 */

ContributorDashboard.propTypes = {
	meta: PropTypes.shape(),
	data: PropTypes.shape()
};
ContributorDashboard.defaultProps = {
	meta: {},
	data: {}
};


/**
 * Exports
 */

export default ContributorDashboard;
